<script lang="ts">
  import { push } from 'svelte-spa-router';
  import { AuthService } from '../../lib/api/auth.service';
  import AuthPageLayout from '../../components/auth/AuthPageLayout.svelte';

  let email = '';
  let loading = false;
  let submitted = false;
  let emailError = '';
  let formError = '';

  function validateEmail(): boolean {
    emailError = '';
    formError = '';

    if (!email.trim()) {
      emailError = 'Email address is required';
      return false;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email.trim())) {
      emailError = 'Please enter a valid email address';
      return false;
    }

    return true;
  }

  async function handleSubmit() {
    if (!validateEmail()) return;

    loading = true;
    formError = '';

    try {
      const result = await AuthService.resetPassword({ email: email.trim() });

      if (result.success) {
        submitted = true;
      } else {
        formError = result.message || 'Failed to send reset email. Please try again.';
      }
    } catch (error: any) {
      console.error('Password reset error:', error);
      formError =
        error?.response?.data?.message || 'Failed to send reset email. Please try again.';
    } finally {
      loading = false;
    }
  }
</script>

{#if submitted}
  <AuthPageLayout title="Check your email" subtitle="We've sent you a password reset link.">
    <div class="auth-status-panel">
      <svg
        width="56"
        height="56"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        class="auth-status-icon success"
        aria-hidden="true"
      >
        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
        <polyline points="22 4 12 14.01 9 11.01"></polyline>
      </svg>
      <p>We've sent a password reset link to <strong>{email}</strong></p>
      <p>Please check your inbox and click the link to reset your password. The link expires in 24 hours.</p>
      <div class="auth-actions">
        <button type="button" class="auth-btn" on:click={() => push('/login')}>
          Back to sign in
        </button>
      </div>
    </div>
  </AuthPageLayout>
{:else}
  <AuthPageLayout
    title="Forgot password"
    subtitle="Enter your email and we'll send you a link to reset your password."
  >
    <form on:submit|preventDefault={handleSubmit}>
      {#if formError}
        <div class="auth-form-error" role="alert">{formError}</div>
      {/if}

      <section class="auth-form-section" aria-labelledby="forgot-email-heading">
        <h2 id="forgot-email-heading" class="auth-form-section-title">Your account</h2>
        <div class="auth-form-section-body">
          <div class="auth-form-group">
            <label for="forgot-password-email">
              Email address <span class="auth-required" aria-label="required">*</span>
            </label>
            <input
              type="email"
              id="forgot-password-email"
              class="auth-input"
              class:error={emailError}
              bind:value={email}
              placeholder="Enter your email address"
              disabled={loading}
              required
              autocomplete="email"
              aria-invalid={!!emailError}
              aria-describedby={emailError ? 'forgot-email-error' : undefined}
              on:input={() => { emailError = ''; formError = ''; }}
            />
            {#if emailError}
              <p class="auth-field-error" id="forgot-email-error" role="alert">{emailError}</p>
            {/if}
          </div>
        </div>
      </section>

      <div class="auth-actions">
        <button type="submit" class="auth-btn" disabled={loading || !email.trim()}>
          {loading ? 'Sending...' : 'Send reset link'}
        </button>
        <button
          type="button"
          class="auth-btn-secondary"
          on:click={() => push('/login')}
          disabled={loading}
        >
          Back to sign in
        </button>
      </div>
    </form>

    <svelte:fragment slot="footer">
      Remember your password?
      <a href="/login" on:click|preventDefault={() => push('/login')}>Sign in</a>
    </svelte:fragment>
  </AuthPageLayout>
{/if}
